<template>
    <div>
        <strong>{{ 'Appointment suggestions' | trans}}:</strong><br>
        <div
                v-if="errorMessage !== null"
                class="text-danger">
            {{ errorMessage }}
        </div>
        <Spinner
                v-else-if="fetching"
                v-bind:title="'Loading suggestions...' | trans">
        </Spinner>
        <template v-else>
            <div
                    v-if="slots !== null && slots.length === 0"
                    class="text-danger">
                {{ 'No slots !' | trans }}
                (
                <RouterLink to="availabilities">
                    <i class="icon fa fa-calendar"></i>&nbsp;
                    <span class="title">{{ 'make space in your availabilities' | trans }}</span>
                </RouterLink>
                )
            </div>
            <template v-else>
                <p>
                    {{ 'Use the 1st cross to refuse the whole day, or the 2nd cross to refuse the time gap.' | trans }}
                </p>
                <table class="table">
                    <tr v-for="(slot, index) in shownSlots">
                        <td>
                            <span v-bind:style="isTimeslotDiscarded(slot) || isDayDiscarded(slot) ? discardedStyle : {}">
                                {{ formatDay(slot.day) }}
                                <i
                                        v-if="index === shownSlots.length - 1 && !allSlotsAreShown"
                                        v-on:click="discardDay(slot)"
                                        class="fa fa-times fa-2x">
                                </i>
                            </span>
                        </td>
                        <td>
                            <span v-bind:style="isTimeslotDiscarded(slot) ? discardedStyle : {}">
                                {{ formatSlotTimeInterval(slot) }}
                                <i
                                        v-if="index === shownSlots.length - 1 && !allSlotsAreShown"
                                        v-on:click="discardTimeslot(slot)"
                                        class="fa fa-times fa-2x">
                                </i>
                            </span>
                        </td>
                        <td>
                            {{ slot.virtualDistance.toFixed(0) }} km
                        </td>
                        <td>
                            <i
                                    v-on:click="$emit('slot:selected', slot)"
                                    v-bind:style="{color: 'green'}"
                                    class="fa fa-thumbs-up fa-2x">
                            </i>
                        </td>
                    </tr>
                </table>
                <div
                        v-if="allSlotsAreShown"
                        class="text-warning">
                    {{ 'No more suggestions to show' | trans }}
                    (
                    <RouterLink to="availabilities">
                        <i class="icon fa fa-calendar"></i>&nbsp;
                        <span class="title">{{ 'make space in your availabilities' | trans }}</span>
                    </RouterLink>
                    )
                </div>
            </template>
        </template>
    </div>
</template>

<script>
    import moment from 'moment';
    import Spinner from '../Spinner';

    export default {
        components: {Spinner},
        props: {
            originalJobId: Number,
            durationRequired: Number,
            customerAddressZipCode: String
        },
        data() {
            return {
                slots: null,
                discardedDays: {},
                discardedTimeslots: {},
                fetching: false,
                errorMessage: null
            };
        },
        async created() {
            await this.tryTofetchSlots();
        },
        methods: {
            async tryTofetchSlots() {
                this.slots = null;
                this.discardedDays = {};
                this.discardedTimeslots = {};
                try {
                    this.fetching = true;
                    this.slots = await this.$api.slot.suggest(this.originalJobId, this.durationRequired, this.customerAddressZipCode);
                    this.fetching = false;
                    this.errorMessage = null;
                }
                catch (error) {
                    if (this.$api.is401(error)) {
                        // in case of 401, just abort javascript execution
                        return;
                    }
                    if (this.$api.isCancel(error)) {
                        // request canceled programmatically, let the new request do the job
                        return;
                    }
                    if (this.$api.isNoConnectivity(error)) {
                        this.errorMessage = this.$options.filters.trans('No connection ?');
                    } else {
                        this.$bugsnagClient.notify(error);
                        this.errorMessage = this.$options.filters.trans('Unknown error');
                    }
                    this.fetching = false;
                }
            },
            discardDay(slot) {
                this.$set(this.discardedDays, slot.day, slot);
            },
            discardTimeslot(slot) {
                if (this.discardedTimeslots[slot.day] === undefined) {
                    this.$set(this.discardedTimeslots, slot.day, {});
                }
                this.$set(this.discardedTimeslots[slot.day], slot.timeslotIndex, slot);
            },
            isDayDiscarded(slot) {
                return (
                    this.discardedDays[slot.day] !== undefined
                    || (
                        this.todayOnly
                        && !this.isToday(slot.day)
                    )
                );
            },
            isTimeslotDiscarded(slot) {
                let isTimeslotDiscarded = false;
                if (this.discardedTimeslots[slot.day] !== undefined) {
                    for (const timeslotIndex in this.discardedTimeslots[slot.day]) {
                        if (timeslotIndex >= slot.timeslotIndex - 5 && timeslotIndex <= slot.timeslotIndex + 5) {
                            isTimeslotDiscarded = true;
                            break;
                        }
                    }
                }
                return isTimeslotDiscarded;
            },
            isDiscarded(slot) {
                return (
                    this.isDayDiscarded(slot)
                    || this.isTimeslotDiscarded(slot)
                );
            },
            // ----
            formatDay(day) {
                const momentDay = moment(day);
                if (momentDay.isSame(Date.now(), 'day')) {
                    return this.$options.filters.trans('Today');
                }
                if (momentDay.isSame(moment().add(1, 'day'), 'day')) {
                    return this.$options.filters.trans('Tomorrow');
                }
                return momentDay.format('dddd D MMMM');
            },
            formatSlotTimeInterval(slot) {
                const parsedTime = moment(slot.time, 'HH:mm:ss');
                return parsedTime.format('H:mm') + ' -> ' + parsedTime.add(2, 'hours').format('H:mm');
            }
        },
        computed: {
            shownSlots() {
                if (this.slots === null) {
                    return null;
                }
                const shownSlots = [];
                for (let i = 0; i < this.slots.length; i++) {
                    const slot = this.slots[i];
                    if (this.isDiscarded(slot)) {
                        shownSlots.push(slot);
                    }
                    else {
                        shownSlots.push(slot);
                        break;
                    }
                }
                return shownSlots;
            },
            discardedStyle() {
                return {
                    textDecoration: 'line-through',
                    color: 'red'
                };
            },
            allSlotsAreShown() {
                return this.slots !== null && this.shownSlots.length === this.slots.length;
            },
        },
        watch: {
            async durationRequired() {
                await this.tryTofetchSlots();
            }
        },
    }
</script>

<style scoped>
    td {
        padding-bottom: 15px;
        font-size: 13px;
    }
</style>